import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bed, Car, MapPin } from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";
import { DemoNav, WorkflowStrip, DemoFooter } from "@/components/DemoShell";
import { DemoChat } from "@/components/DemoChat";
import { Reveal, MaskedLine } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTIES = [
    { id: "moorland", name: "The Moorland House", location: "Chiltern Hills", price: "£1,450,000", beds: 5, parking: "Triple garage + drive", img: "/assets/img/property-1.jpg" },
    { id: "gallery", name: "Gallery Penthouse", location: "City Centre", price: "£895,000", beds: 3, parking: "2 secure spaces", img: "/assets/img/property-3.jpg" },
    { id: "interior", name: "The Stone Barn", location: "Oxfordshire", price: "£775,000", beds: 4, parking: "Double garage", img: "/assets/img/property-2.jpg" },
];

const CHAT = {
    greeting: "Welcome to HIGHTS Property. I'm the property assistant — ask me about any listing, or arrange a viewing.",
    suggestions: [
        { q: "How much is the property?", a: "The Moorland House is guide price £1,450,000, the Gallery Penthouse £895,000, and the Stone Barn £775,000. Which one would you like to know more about?" },
        { q: "What are the bedrooms?", a: "The Moorland House has five double bedrooms (three en-suite), the Penthouse has three, and the Stone Barn four. Shall I send you the full floor plans?" },
        { q: "Is there parking?", a: "Yes — the Moorland House has a triple garage and private drive, the Penthouse two secure underground spaces, and the Stone Barn a double garage. Would you like to arrange a viewing?" },
        { q: "Can I arrange a viewing?", a: "Of course. This week we have Thursday 11:30 or Saturday 10:00 available at The Moorland House. May I take your name and number to confirm?" },
    ],
    handoff: "Lovely — I've noted your interest. Pop your details into the viewing form and everything from here is automatic: confirmation, reminders and follow-up.",
};

export default function PropertyDemo() {
    const [selected, setSelected] = useState(PROPERTIES[0].id);
    const [form, setForm] = useState({ name: "", email: "", phone: "", date: "" });
    const [state, setState] = useState("idle");
    const [workflow, setWorkflow] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setState("sending");
        try {
            const prop = PROPERTIES.find((p) => p.id === selected);
            await axios.post(`${API}/enquiries`, {
                name: form.name, email: form.email, phone: form.phone,
                business: "HIGHTS PROPERTY (demo)", industry: "Property & estates",
                main_challenge: `Viewing request: ${prop.name}${form.date ? ` on ${form.date}` : ""}`,
                desired_outcome: "Property viewing and purchase enquiry",
                budget: "15000-plus", preferred_contact: "Email", source: "demo-property",
            });
            setState("success");
            setWorkflow(true);
        } catch {
            setState("error");
            toast.error("Something went wrong — please try again.");
        }
    };

    const inputCls = "h-12 w-full border border-white/15 bg-black px-4 text-sm text-white placeholder:text-white/30 focus:border-white/45 focus:outline-none";
    const labelCls = "mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/45";

    return (
        <div className="min-h-screen bg-base" data-testid="property-demo">
            <SEO title="HIGHTS PROPERTY — Interactive Estate Agency Demo | HIGHTS" description="A working demonstration of a HIGHTS estate agency growth system: listings, AI property assistant, viewing bookings and automated CRM journeys." path="/demos/property" />
            <DemoNav name="HIGHTS PROPERTY" sector="Estate agency demo" />

            <section className="relative flex min-h-[88vh] items-end overflow-hidden pt-16">
                <img src="/assets/img/property-1.jpg" alt="The Moorland House at dusk" className="absolute inset-0 h-full w-full object-cover opacity-55" />
                <div className="absolute inset-0 bg-gradient-to-t from-base via-black/40 to-black/30" />
                <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-20 md:px-10">
                    <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/60">Exceptional homes · Engineered service</p>
                    <h1 className="mt-5 font-display font-black tracking-tighter">
                        <MaskedLine delay={0.2}><span className="block text-5xl leading-[0.95] text-white sm:text-7xl">HIGHTS</span></MaskedLine>
                        <MaskedLine delay={0.4}><span className="block text-5xl leading-[0.95] text-white/55 sm:text-7xl">PROPERTY</span></MaskedLine>
                    </h1>
                    <Reveal delay={0.6}>
                        <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-white/70">
                            Every enquiry answered instantly, every viewing booked automatically, every buyer followed up — this is an estate agency running on a HIGHTS growth system.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-24" data-testid="property-listings">
                <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                    <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/40">Current listings</p>
                    <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
                        {PROPERTIES.map((p, i) => (
                            <Reveal key={p.id} delay={i * 0.1}>
                                <article className="group border border-white/12 bg-surface" data-testid={`property-card-${p.id}`}>
                                    <div className="relative aspect-[4/3] overflow-hidden">
                                        <img src={p.img} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                                        <span className="absolute left-4 top-4 bg-black/70 px-3 py-1.5 font-mono text-[10px] tracking-widest text-white backdrop-blur-sm">{p.price}</span>
                                    </div>
                                    <div className="p-6">
                                        <h2 className="font-display text-xl font-bold text-white">{p.name}</h2>
                                        <p className="mt-1 flex items-center gap-1.5 text-xs text-white/45"><MapPin size={13} /> {p.location}</p>
                                        <div className="mt-4 flex gap-4 text-xs text-white/55">
                                            <span className="flex items-center gap-1.5"><Bed size={14} /> {p.beds} bedrooms</span>
                                            <span className="flex items-center gap-1.5"><Car size={14} /> {p.parking}</span>
                                        </div>
                                        <button
                                            onClick={() => { setSelected(p.id); document.getElementById("property-enquiry")?.scrollIntoView({ behavior: "smooth" }); }}
                                            data-testid={`property-viewing-${p.id}`}
                                            className="mt-6 w-full border border-white/25 py-3 text-[11px] font-bold uppercase tracking-[0.22em] text-white transition-colors hover:bg-white hover:text-black"
                                        >
                                            Arrange a viewing
                                        </button>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-white/10 bg-void py-24">
                <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-6 px-5 md:px-10 lg:grid-cols-2">
                    <Reveal>
                        <div className="flex h-[560px] flex-col border border-white/12 bg-surface">
                            <DemoChat title="Property Assistant" subtitle="HIGHTS PROPERTY · live AI" greeting={CHAT.greeting} suggestions={CHAT.suggestions} handoff={CHAT.handoff} live agentId="property" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div id="property-enquiry" className="border border-white/12 bg-surface p-8 md:p-10">
                            <h2 className="font-display text-2xl font-black tracking-tight text-white">Request a viewing</h2>
                            <p className="mt-2 text-sm font-light text-white/50">A real enquiry — watch the automated journey run after you submit.</p>
                            {state === "success" ? (
                                <div className="mt-8 border border-white/15 bg-black/50 p-6" data-testid="property-enquiry-success">
                                    <p className="font-display text-lg font-bold text-white">Viewing request received.</p>
                                    <p className="mt-2 text-sm font-light text-white/55">Confirmation is on its way — and the automated journey below just ran.</p>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="property-enquiry-form">
                                    <div>
                                        <label className={labelCls} htmlFor="pd-prop">Property</label>
                                        <select id="pd-prop" data-testid="property-select" value={selected} onChange={(e) => setSelected(e.target.value)} className={inputCls}>
                                            {PROPERTIES.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.price}</option>)}
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <div>
                                            <label className={labelCls} htmlFor="pd-name">Name *</label>
                                            <input id="pd-name" data-testid="property-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Your name" />
                                        </div>
                                        <div>
                                            <label className={labelCls} htmlFor="pd-email">Email *</label>
                                            <input id="pd-email" data-testid="property-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@email.com" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <div>
                                            <label className={labelCls} htmlFor="pd-phone">Phone</label>
                                            <input id="pd-phone" data-testid="property-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="+44…" />
                                        </div>
                                        <div>
                                            <label className={labelCls} htmlFor="pd-date">Preferred date</label>
                                            <input id="pd-date" data-testid="property-date" type="date" style={{ colorScheme: "dark" }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                                        </div>
                                    </div>
                                    <button type="submit" disabled={state === "sending"} data-testid="property-submit"
                                        className="w-full bg-white py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-white/85 disabled:opacity-50">
                                        {state === "sending" ? "Sending…" : "Request viewing"}
                                    </button>
                                </form>
                            )}
                            <div className="mt-8">
                                <WorkflowStrip active={workflow} />
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
            <DemoFooter name="HIGHTS PROPERTY" />
        </div>
    );
}
